/**
 * AdminSsoRolloutPage — SpLink admin dashboard widget for the 34-site
 * SSO rollout. Live color-coded grid showing:
 *
 *   🟢 provisioned — client_id/secret dropped into sso_credentials.json
 *   🟡 pending      — awaiting SocialPulse SSO team's response JSON
 *
 * Also gives one-click "Copy envelope" per site so the admin never
 * greps the master doc during rollout.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { CheckCircle2, Clock, Copy, RefreshCw } from "lucide-react";

export default function AdminSsoRolloutPage() {
  const [status, setStatus] = useState(null);
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const headers = { Authorization: `Bearer ${token}` };
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/sso/status`,    { headers }),
        axios.get(`${API}/admin/sso/envelopes`, { headers }),
      ]);
      setStatus(s.data);
      setEnvelopes(e.data.items || []);
    } catch (err) {
      // Silent — likely 401 for non-admin. UI shows an inline hint.
      setStatus({ error: err.response?.status || err.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function copyEnvelope(slug, envelope) {
    try {
      await navigator.clipboard.writeText(envelope);
      setCopied(slug);
      setTimeout(() => setCopied(null), 2000);
    } catch {}
  }

  if (loading) {
    return <div className="p-12 text-center text-slate-500">Loading rollout status…</div>;
  }
  if (status?.error) {
    return (
      <div className="p-12 text-center text-slate-500" data-testid="wp-admin-sso-forbidden">
        This dashboard requires admin auth. Sign in with an admin account.
      </div>
    );
  }

  const total       = status?.total_sites || 0;
  const provisioned = status?.provisioned_count || 0;
  const pending     = status?.pending_count || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="wp-admin-sso-rollout">
      <div className="mb-8 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-1">SpLink Admin</div>
          <h1 className="text-3xl font-semibold text-slate-900">Ecosystem SSO rollout</h1>
          <p className="text-sm text-slate-500 mt-1">
            {total} sister sites · {provisioned} provisioned · {pending} pending
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm text-slate-600 hover:bg-slate-50"
          data-testid="wp-admin-sso-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-8 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all duration-500"
          style={{ width: `${total ? (provisioned / total) * 100 : 0}%` }}
          data-testid="wp-admin-sso-progress"
        />
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {envelopes.map((e) => (
          <div
            key={e.slug}
            className={`rounded-xl border p-4 relative overflow-hidden transition ${
              e.provisioned
                ? "border-emerald-200 bg-emerald-50/40"
                : "border-amber-200 bg-amber-50/40"
            }`}
            data-testid={`wp-admin-sso-row-${e.slug}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  {e.provisioned ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-amber-600" />
                  )}
                  <span className={`text-xs font-medium uppercase tracking-wider ${
                    e.provisioned ? "text-emerald-700" : "text-amber-700"
                  }`}>
                    {e.provisioned ? "Provisioned" : "Pending"}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-1">#{e.num}</span>
                </div>
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {e.client_name}
                </div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {e.slug}
                </div>
                <div className="text-[10px] text-slate-400 truncate mt-0.5">
                  {e.callback.replace(/^https:\/\//,'').replace(/\/auth\/callback$/,'')}
                </div>
              </div>
              <button
                onClick={() => copyEnvelope(e.slug, e.envelope)}
                className="flex-shrink-0 text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1"
                data-testid={`wp-admin-sso-copy-${e.slug}`}
              >
                <Copy className="w-3 h-3" />
                {copied === e.slug ? "Copied" : "Envelope"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {envelopes.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          No sites in the registry. Check /app/docs/ecosystem-sso-onboarding.md.
        </div>
      )}
    </div>
  );
}
